/**
 * PnlTab.jsx — per-merchant profit-and-loss snapshot.
 *
 * Pulls /api/3pl/pnl and renders a sortable table with: pallets,
 * monthly storage estimate, last-30d charges, ratio. Sorted by
 * charges_30d DESC by default so the AR-chasing operator sees top
 * contributors first.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { ArrowClockwise, Warning } from '@phosphor-icons/react';
import { get3plPnl } from '../../lib/api';
import { fmt$ } from './utils';
import MerchantDrillDownDialog from './MerchantDrillDownDialog';

export default function PnlTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillMerchant, setDrillMerchant] = useState(null);

  const fetchPnl = async () => {
    setLoading(true);
    try {
      const r = await get3plPnl();
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load P&L');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchPnl(); }, []);

  if (loading || !data) return <div className="text-muted-foreground p-6">Loading P&L…</div>;

  const { merchants, totals, lookback_days } = data;

  const Tile = ({ label, value, sub, color = 'sky' }) => {
    const cls = {
      sky: 'border-sky-200 bg-sky-50 text-sky-800',
      emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
      amber: 'border-amber-200 bg-amber-50 text-amber-800',
    }[color];
    return (
      <div className={`border ${cls} p-4`}>
        <div className="text-xs uppercase tracking-wider font-semibold opacity-80">{label}</div>
        <div className="text-2xl font-bold font-mono mt-1">{value}</div>
        {sub && <div className="text-[11px] opacity-70 mt-1">{sub}</div>}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Per-merchant P&L · last {lookback_days} days</h2>
        <Button size="sm" variant="outline" onClick={fetchPnl} data-testid="pnl-refresh">
          <ArrowClockwise size={14} />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="pnl-totals">
        <Tile label="Total pallets" value={totals.pallets} color="sky" />
        <Tile label="Monthly storage est." value={fmt$(totals.monthly_storage_est)} sub="pallets × per-pallet rate" color="amber" />
        <Tile label={`Charges in ${lookback_days}d`} value={fmt$(totals.charges_30d)} sub="non-voided line items" color="emerald" />
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-left">
              <th className="p-2">Merchant</th>
              <th className="p-2 text-right">Pallets</th>
              <th className="p-2 text-right">Per pallet/mo</th>
              <th className="p-2 text-right">Storage est. (mo)</th>
              <th className="p-2 text-right">Charges {lookback_days}d</th>
              <th className="p-2 text-right">Ratio</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {merchants.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                No active merchants yet.
              </td></tr>
            )}
            {merchants.map((m) => {
              const ratio = m.revenue_to_storage_ratio;
              const ratioColor = ratio === null
                ? 'text-muted-foreground'
                : ratio >= 1
                  ? 'text-emerald-700'
                  : ratio >= 0.5
                    ? 'text-amber-700'
                    : 'text-rose-700';
              return (
                <tr key={m.merchant_id}
                  className="border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/40"
                  onClick={() => setDrillMerchant(m)}
                  data-testid={`pnl-row-${m.merchant_id}`}>
                  <td className="p-2">
                    <div className="font-semibold flex items-center gap-1.5 hover:underline">
                      {m.name}
                      {m.is_default && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-200">Default</span>}
                      {!m.has_rate_card && (
                        <span title="No rate card configured" className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                          <Warning size={10} weight="bold" />
                          no rate card
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-2 text-right font-mono">{m.pallets}</td>
                  <td className="p-2 text-right font-mono text-xs">{fmt$(m.per_pallet_per_month)}</td>
                  <td className="p-2 text-right font-mono">{fmt$(m.monthly_storage_est)}</td>
                  <td className="p-2 text-right font-mono font-bold">{fmt$(m.charges_30d)}</td>
                  <td className={`p-2 text-right font-mono font-bold ${ratioColor}`}>
                    {ratio === null ? '—' : `${ratio}×`}
                  </td>
                  <td className="p-2 text-xs text-muted-foreground">
                    {ratio === null ? '' : ratio >= 1 ? 'above water' : ratio >= 0.5 ? 'thin margin' : 'eating storage'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-muted-foreground">
        Pallets = occupied bins for this merchant. Default merchant absorbs un-tagged inventory.
        Storage estimate uses the current rate card's <code>per_pallet_per_month</code> rate.
        Charges include all non-voided line items in the lookback window (storage / inbound / outbound / VAS).
        <span className="font-semibold"> Click any merchant row for a 30d drill-down.</span>
      </div>

      <MerchantDrillDownDialog
        open={!!drillMerchant}
        onOpenChange={(o) => { if (!o) setDrillMerchant(null); }}
        merchant={drillMerchant}
      />
    </div>
  );
}
